// A folder on the user's disk, opened read-only through the File System Access API (Chrome, Edge).
// The app keeps a live view: it notices new, removed and edited files, for example when an agent
// writes a sketch into .modelviewer/ or adds a migration.
import { IGNORED_DIRECTORIES, isCheckout, type SourceFileEntry } from '@modelviewer/core';
import { isRelevantFile, type SourceChange, type WatchTargets, type WorkspaceSource } from './types';

const POLL_MS = 2000;
const WALK_CONCURRENCY = 12;

/** A folder waiting to be read: its handle, its path with a trailing `/`, and whether a folder above it is a git checkout. */
type Queued = [FileSystemDirectoryHandle, string, boolean];

export class LocalFolderSource implements WorkspaceSource {
  readonly kind = 'folder' as const;
  readonly live = true;
  readonly label: string;
  private readonly dirCache = new Map<string, FileSystemDirectoryHandle>();

  constructor(
    readonly id: string,
    readonly root: FileSystemDirectoryHandle,
  ) {
    this.label = root.name;
    this.dirCache.set('', root);
  }

  async list(onProgress?: (count: number) => void): Promise<SourceFileEntry[]> {
    const out: SourceFileEntry[] = [];
    const queue: Queued[] = [[this.root, '', false]];
    let active = 0;
    await new Promise<void>((resolve, reject) => {
      const pump = () => {
        if (queue.length === 0 && active === 0) {
          resolve();
          return;
        }
        while (active < WALK_CONCURRENCY && queue.length > 0) {
          active++;
          this.readDirectory(queue.shift()!, queue, out)
            .then(() => {
              active--;
              onProgress?.(out.length);
              pump();
            })
            .catch(reject);
        }
      };
      pump();
    });
    return out.sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));
  }

  private async readDirectory([dir, prefix, insideCheckout]: Queued, queue: Queued[], out: SourceFileEntry[]): Promise<void> {
    const entries: FileSystemHandle[] = [];
    for await (const entry of dir.values()) entries.push(entry);
    const checkout = isCheckout(entries.map((e) => e.name));
    if (checkout && insideCheckout) return;
    for (const entry of entries) {
      if (entry.kind === 'directory') {
        if (IGNORED_DIRECTORIES.has(entry.name)) continue;
        const path = `${prefix}${entry.name}/`;
        this.dirCache.set(path.slice(0, -1), entry as FileSystemDirectoryHandle);
        queue.push([entry as FileSystemDirectoryHandle, path, insideCheckout || checkout]);
      } else {
        const path = `${prefix}${entry.name}`;
        if (isRelevantFile(path)) out.push({ path });
      }
    }
  }

  private async directory(path: string): Promise<FileSystemDirectoryHandle> {
    const cached = this.dirCache.get(path);
    if (cached) return cached;
    const slash = path.lastIndexOf('/');
    const parent = await this.directory(slash < 0 ? '' : path.slice(0, slash));
    const handle = await parent.getDirectoryHandle(path.slice(slash + 1));
    this.dirCache.set(path, handle);
    return handle;
  }

  private async file(path: string): Promise<File> {
    const slash = path.lastIndexOf('/');
    const dir = await this.directory(slash < 0 ? '' : path.slice(0, slash));
    const handle = await dir.getFileHandle(path.slice(slash + 1));
    return handle.getFile();
  }

  async read(path: string): Promise<string> {
    return (await this.file(path)).text();
  }

  watch(targets: WatchTargets, onChange: (change: SourceChange) => void): () => void {
    return new FolderWatcher(this, targets, onChange).start();
  }

  /** File names (relative paths) directly in a folder, or recursively for the workspace folder. */
  async listFolder(path: string, recursive: boolean): Promise<string[]> {
    let dir: FileSystemDirectoryHandle;
    try {
      dir = await this.directory(path);
    } catch {
      this.dirCache.delete(path);
      return [];
    }
    const out: string[] = [];
    const walk = async (d: FileSystemDirectoryHandle, prefix: string) => {
      for await (const entry of d.values()) {
        const p = prefix ? `${prefix}/${entry.name}` : entry.name;
        if (entry.kind === 'file') out.push(p);
        else if (recursive && !IGNORED_DIRECTORIES.has(entry.name)) await walk(entry as FileSystemDirectoryHandle, p);
      }
    };
    await walk(dir, path);
    return out;
  }

  /** Modified time and size, or null if the file is gone. */
  async signature(path: string): Promise<string | null> {
    try {
      const f = await this.file(path);
      return `${f.lastModified}:${f.size}`;
    } catch {
      return null;
    }
  }

  /** Clears cached directory handles below `path` (after deletions or renames). */
  forget(path: string): void {
    for (const key of [...this.dirCache.keys()]) if (key !== '' && (key === path || key.startsWith(`${path}/`))) this.dirCache.delete(key);
  }
}

/**
 * Detects changes to watched folders and files. Uses FileSystemObserver where the browser has it,
 * and otherwise polls every two seconds while the tab is visible.
 */
class FolderWatcher {
  private readonly signatures = new Map<string, string | null>();
  private readonly listings = new Map<string, Set<string>>();
  private timer: number | undefined;
  private observer: FileSystemObserverInstance | null = null;
  private stopped = false;
  private busy = false;
  private pending = false;

  constructor(
    private readonly source: LocalFolderSource,
    private readonly targets: WatchTargets,
    private readonly onChange: (change: SourceChange) => void,
  ) {}

  start(): () => void {
    void this.snapshot().then(() => {
      if (this.stopped) return;
      const Observer = (globalThis as unknown as { FileSystemObserver?: FileSystemObserverConstructor }).FileSystemObserver;
      if (Observer) {
        this.observer = new Observer(() => this.check());
        void this.observeTargets().catch(() => {
          this.observer?.disconnect();
          this.observer = null;
          this.poll();
        });
        // A slower safety poll for missed observations and folders created later.
        this.timer = window.setInterval(() => {
          if (document.visibilityState === 'visible') this.check();
        }, POLL_MS * 3);
      } else {
        this.poll();
      }
    });
    return () => {
      this.stopped = true;
      window.clearInterval(this.timer);
      this.observer?.disconnect();
    };
  }

  private poll(): void {
    this.timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') this.check();
    }, POLL_MS);
  }

  private async observeTargets(): Promise<void> {
    for (const folder of this.targets.folders) {
      let dir: FileSystemDirectoryHandle | null = this.source.root;
      for (const segment of folder.split('/').filter(Boolean)) {
        dir = dir ? await dir.getDirectoryHandle(segment).catch(() => null) : null;
      }
      // Folders that do not exist yet (such as .modelviewer before an agent creates it) are caught by the safety poll.
      if (dir && this.observer) await this.observer.observe(dir, { recursive: isWorkspaceFolder(folder) });
    }
  }

  private async snapshot(): Promise<void> {
    for (const folder of this.targets.folders) {
      this.listings.set(folder, new Set(await this.source.listFolder(folder, isWorkspaceFolder(folder))));
    }
    const files = new Set(this.targets.files);
    for (const names of this.listings.values()) for (const n of names) files.add(n);
    await Promise.all([...files].map(async (f) => this.signatures.set(f, await this.source.signature(f))));
  }

  private check(): void {
    if (this.busy) {
      this.pending = true;
      return;
    }
    this.busy = true;
    void this.diff()
      .then((change) => {
        if (!this.stopped && (change.added.length || change.removed.length || change.changed.length)) this.onChange(change);
      })
      .finally(() => {
        this.busy = false;
        if (this.pending && !this.stopped) {
          this.pending = false;
          this.check();
        }
      });
  }

  private async diff(): Promise<SourceChange> {
    const change: SourceChange = { added: [], removed: [], changed: [] };
    for (const folder of this.targets.folders) {
      const before = this.listings.get(folder) ?? new Set<string>();
      this.source.forget(folder);
      const after = new Set(await this.source.listFolder(folder, isWorkspaceFolder(folder)));
      for (const f of after) {
        if (!before.has(f)) {
          change.added.push(f);
          this.signatures.set(f, await this.source.signature(f));
        }
      }
      for (const f of before) {
        if (!after.has(f)) {
          change.removed.push(f);
          this.signatures.delete(f);
        }
      }
      this.listings.set(folder, after);
    }
    const added = new Set(change.added);
    await Promise.all(
      [...this.signatures.keys()].map(async (f) => {
        if (added.has(f)) return;
        const sig = await this.source.signature(f);
        if (sig !== this.signatures.get(f)) {
          this.signatures.set(f, sig);
          if (sig === null) change.removed.push(f);
          else change.changed.push(f);
        }
      }),
    );
    return change;
  }
}

function isWorkspaceFolder(folder: string): boolean {
  return folder === '.modelviewer' || folder.endsWith('/.modelviewer');
}

export function supportsFolderAccess(): boolean {
  return typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';
}

/** Checks, and if needed asks for, read permission. Must run inside a user gesture to prompt. */
export async function ensureReadPermission(handle: FileSystemDirectoryHandle, prompt: boolean): Promise<boolean> {
  const state = (await handle.queryPermission?.({ mode: 'read' })) ?? 'granted';
  if (state === 'granted') return true;
  if (!prompt) return false;
  return ((await handle.requestPermission?.({ mode: 'read' })) ?? 'denied') === 'granted';
}
